import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

import * as moment from 'moment';

import { Router, ActivatedRoute, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { SessionService } from './session.service';

import { environment } from '../../environments/environment';
import { HttpErrorHandler, HandleError } from './http-error-handler.service';

import { UserInfo, IpAddress, LogonRequest, LogonUserInformation } from '../models/user';
import { VerifyService } from './verify.service';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private _logging = environment.logging;
    private _apiSettings = environment.restAPI;
    private _userSettings = environment.user;
    private _baseServiceUrl = '';
    private _handleError: HandleError;

    private userInfoSessionName = 'userInfo';

    private _userInfo: UserInfo;

    userInfoSubject: BehaviorSubject<UserInfo>;
    userInfo: Observable<UserInfo>;

    //01/19/2026 RA - Logon User Information for Multifactor Verify Component
    private logonUserInfoSource =
    new BehaviorSubject<LogonUserInformation | null>(null);
    private logonUserInfoSessionName = 'logonUserInfo';

    logonUserInfo$ = this.logonUserInfoSource.asObservable();

    setLogonUserInformation(info: LogonUserInformation): void {
      this.logonUserInfoSource.next(info);
      this._sessionService.setObjectToSession(
         this.logonUserInfoSessionName, info
     );
    }

    getLogonUserInformation(): LogonUserInformation | null {
      return this.logonUserInfoSource.value;
    }

    clearLogonUserInformation(): void {
      this.logonUserInfoSource.next(null);
      this._sessionService.removeValueFromSession(this.logonUserInfoSessionName);
    }

    constructor(
        @Inject('BASE_URL') baseUrl: string,
        private _route: ActivatedRoute,
        private _router: Router,
        private _httpClient: HttpClient,
        private _sessionService: SessionService,
        httpErrorHandler: HttpErrorHandler
    ) {
        this._baseServiceUrl = (this._apiSettings.useStaticUrl ? this._apiSettings.staticUrl : baseUrl) + this._apiSettings.urlSuffix;
        this._handleError = httpErrorHandler.createHandleError('UserService');

        const sessionUserInfo: UserInfo = this._sessionService.getObjectFromSession(this.userInfoSessionName);
        if (sessionUserInfo !== null) {
            sessionUserInfo.sessionCreateDate = new Date(sessionUserInfo.sessionCreateDate);
            sessionUserInfo.sessionLastActiveDate = new Date(sessionUserInfo.sessionLastActiveDate);
            sessionUserInfo.accessTokenExpiresAtUtc = sessionUserInfo.accessTokenExpiresAtUtc
                ? new Date(sessionUserInfo.accessTokenExpiresAtUtc)
                : null;

            if (!this.hasValidAccessToken(sessionUserInfo)) {
                sessionUserInfo.authenticated = false;
                sessionUserInfo.sessionToken = null;
            }

            if (this._logging.userTraceLog) {
                console.log('UserService.constructor :: getObjectFromSession = true');
            }

            this._userInfo = sessionUserInfo;
        } else {
            if (this._logging.userTraceLog) {
                console.log('UserService.constructor :: getObjectFromSession = false');
            }

            this._userInfo = this._getNewUserInfo();
        }
        const sessionLogonUserInfo =
          this._sessionService.getObjectFromSession(this.logonUserInfoSessionName);

        this.logonUserInfoSource =
          new BehaviorSubject<LogonUserInformation | null>(
            sessionLogonUserInfo ? sessionLogonUserInfo : null
          );

        this.logonUserInfo$ = this.logonUserInfoSource.asObservable();


        if (this._logging.userTraceLog) {
            console.log('UserService.constructor :: _userInfo = ' + JSON.stringify(this._userInfo));
        }

        this.userInfoSubject = new BehaviorSubject<UserInfo>(this._userInfo);
        this.userInfo = this.userInfoSubject.asObservable();

        this.userInfo.subscribe(response => this._onUserInfoChanged(response), error => this._onError(error));
    }

    public verifyUserName(logonRequest: LogonRequest): Observable<LogonUserInformation> {
        const serviceUrl: string = this._baseServiceUrl + 'login/checkusername';

        if (this._logging.serviceCallTraceLog) {
            console.log('UserService.verifyUserName :: serviceUrl = ' + serviceUrl);
        }

        // return value is the db.userId so any value greater than 0 = verified UserName
        return this._httpClient.post<number>(serviceUrl, logonRequest).pipe(
            retry(this._apiSettings.retryCount),
            catchError(this._handleError('verifyUsername', null))
        );
    }

    public verifyUserNamePassword(logonRequest: LogonRequest): Observable<LogonUserInformation> {
        const serviceUrl: string = this._baseServiceUrl + 'login/checkusernamepassword';

        if (this._logging.serviceCallTraceLog) {
            console.log('UserService.verifyUserNamePassword :: serviceUrl = ' + serviceUrl);
        }

        // return value is the db.userId so any value greater than 0 = verified UserName and Password
        return this._httpClient.post<number>(serviceUrl, logonRequest).pipe(
            retry(this._apiSettings.retryCount),
            catchError(this._handleError('verifyUserNamePassword', null))
        );
    }

    public getNewLogonRequest(): LogonRequest {
        const newLogonRequest: LogonRequest = {
            userName: null,
            password: null,
            ipAddress: null
        };
        return newLogonRequest;
    }

    public getNewLogonUserInformation(): LogonUserInformation {
        const newLogonUserInformation: LogonUserInformation = {
            userId: null,
            participantId: null,
            firstName: null,
            middleInitial: null,
            lastName: null,
            securityQuestionSetupFlag: null,
            retryCount: null,
            lockedOutFlag: null,
            activeFlag: null,
            temporaryPasswordFlag: null,
            temporaryPasswordExpiredFlag: null,
            multifactorEligible: null,
            multifactorEnabled: null,
            multifactorGracePeriod: null,
            lastVerifiedOTP: null,
            multifactorRetryCount: null,
            lastVerifiedIpAddress: null,
            lastAccessed: null,
            createdDate: null,
            daysLastLogin: null,
            createdDateMin: null,
            invalidAddr: null,
            invalidPhone: null,
            invalidEmail: null,
            mfaRequired: false,
            mfaChallengeToken: null,
            accessToken: null,
            accessTokenExpiresAtUtc: null
        };
        return newLogonUserInformation;
    }

    login(
        userId: number,
        userName: string,
        participantId: number,
        firstName: string,
        middleInitial: string,
        lastName: string,
        securityQuestionSetupFlag: boolean,
        accessToken: string,
        accessTokenExpiresAtUtc: Date
    ): void {
        if (userId !== null && userId > 0 && userName !== null && userName.length > 0 && participantId !== null && participantId > 0) {
            this._userInfo = this._getNewUserInfo();

            this._userInfo.userId = userId;
            this._userInfo.userName = userName;
            this._userInfo.participantId = participantId;
            this._userInfo.firstName = firstName;
            this._userInfo.middleInitial = middleInitial;
            this._userInfo.lastName = lastName;
            this._userInfo.securityQuestionSetupFlag = securityQuestionSetupFlag;
            this._userInfo.sessionToken = accessToken;
            this._userInfo.accessTokenExpiresAtUtc = accessTokenExpiresAtUtc ? new Date(accessTokenExpiresAtUtc) : null;
            this._userInfo.authenticated = this.hasValidAccessToken(this._userInfo);
            this._userInfo.sessionCreateDate = new Date();
            this._userInfo.sessionLastActiveDate = new Date();

            if (this._logging.userTraceLog) {
                console.log('UserService.login :: _userInfo = ' + JSON.stringify(this._userInfo));
            }

            this.userInfoSubject.next(this._userInfo);
        }
    }

    completeAuthentication(accessToken: string, accessTokenExpiresAtUtc: Date): void {
        this._userInfo.sessionToken = accessToken;
        this._userInfo.accessTokenExpiresAtUtc = accessTokenExpiresAtUtc ? new Date(accessTokenExpiresAtUtc) : null;
        this._userInfo.authenticated = this.hasValidAccessToken(this._userInfo);
        this._userInfo.sessionLastActiveDate = new Date();
        this.userInfoSubject.next(this._userInfo);
    }

    logout(sessionTimeout: boolean = false): void {
        this._userInfo = this._getNewUserInfo();
        this._userInfo.sessionTimeout = sessionTimeout !== null ? sessionTimeout : false;

        if (this._logging.userTraceLog) {
            console.log('UserService.logout :: _userInfo = ' + JSON.stringify(this._userInfo));
        }

        this.userInfoSubject.next(this._userInfo);
    }

    isLoggedIn(redirectOnNotLoggedIn: boolean = true): boolean {
        let isLoggedIn = this._userInfo.authenticated && this.hasValidAccessToken(this._userInfo);
        const timeoutDate = moment(new Date())
            .subtract(this._userSettings.timeoutInterval, 'm')
            .toDate();
        if (
            this._userInfo.authenticated &&
            this._userInfo.sessionLastActiveDate !== null &&
            this._userInfo.sessionLastActiveDate >= timeoutDate
        ) {
            this._userInfo.sessionLastActiveDate = new Date();
            this.userInfoSubject.next(this._userInfo);

            if (this._logging.userTraceLog) {
                console.log('UserService.isLoggedIn :: sessionLastActiveDate is valid ');
            }
        } else {
            if (this._logging.userTraceLog) {
                console.log('UserService.isLoggedIn :: sessionLastActiveDate is not valid ');
            }

            if (this._userInfo.authenticated) {
                if (this._logging.userTraceLog) {
                    console.log('UserService.isLoggedIn :: force logout ');
                }

                this.logout(true);
            }
            isLoggedIn = false;
        }

        if (this._logging.userTraceLog) {
            console.log('UserService.isLoggedIn :: isLoggedIn = ' + isLoggedIn);
            console.log('UserService.isLoggedIn :: redirectOnNotLoggedIn = ' + redirectOnNotLoggedIn);
            console.log('UserService.isLoggedIn :: _userInfo = ' + JSON.stringify(this._userInfo));
        }

        if (!isLoggedIn && redirectOnNotLoggedIn) {
            if (this._logging.userTraceLog) {
                console.log('UserService.isLoggedIn :: force redirect to login ');
            }

            if (this._logging.navigationTraceLog) {
                console.log('UserService.isLoggedIn');
            }
            this._router.navigate(['login']);
        }

        return isLoggedIn;
    }

    hasSecurityQuestionSetupFlag(): boolean {
        return this._userInfo.securityQuestionSetupFlag !== null && this._userInfo.securityQuestionSetupFlag;
    }

    setSecurityQuestionSetupFlag(securityQuestionSetupFlag: boolean): void {
        if (this._userInfo.authenticated) {
            this._userInfo.securityQuestionSetupFlag = securityQuestionSetupFlag;
            this.userInfoSubject.next(this._userInfo);
        }
    }

    getUserId(): number {
        return this._userInfo.userId !== null && this._userInfo.userId > 0 ? this._userInfo.userId : null;
    }

    getUserName(): string {
        return this._userInfo.userName !== null && this._userInfo.userName.length > 0 ? this._userInfo.userName : null;
    }

    getEmail(): string {
        return this._userInfo.email !== null && this._userInfo.email.length > 0 ? this._userInfo.email : null;
    }

    getFirstName(): string {
        return this._userInfo.firstName !== null && this._userInfo.firstName.length > 0 ? this._userInfo.firstName : null;
    }

    getMiddleInitial(): string {
        return this._userInfo.middleInitial !== null && this._userInfo.middleInitial.length > 0 ? this._userInfo.middleInitial : null;
    }

    getLastName(): string {
        return this._userInfo.lastName !== null && this._userInfo.lastName.length > 0 ? this._userInfo.lastName : null;
    }

    getFullName(): string {
        const fullName: string =
            (this.getFirstName() !== null ? this.getFirstName() : '') + ' ' + (this.getLastName() !== null ? this.getLastName() : '');
        return fullName.trim();
    }

    hasParticipantId(): boolean {
        return this._userInfo.participantId !== null && this._userInfo.participantId > 0;
    }

    getParticipantId(): number {
        return this._userInfo.participantId !== null && this._userInfo.participantId > 0 ? this._userInfo.participantId : null;
    }

    hasNoteId(): boolean {
        return this._userInfo.noteId !== null && this._userInfo.noteId > 0;
    }

    getNoteId(): number {
        return this._userInfo.noteId !== null && this._userInfo.noteId > 0 ? this._userInfo.noteId : null;
    }

    setNoteId(noteId: number): void {
        if (this._userInfo.authenticated) {
            this._userInfo.noteId = noteId;
            this.userInfoSubject.next(this._userInfo);
        }
    }

    clearNoteId(): void {
        if (this._userInfo.authenticated) {
            this._userInfo.noteId = null;
            this.userInfoSubject.next(this._userInfo);
        }
    }

    hasRelationshipType(): boolean {
        return this._userInfo.relationshipType !== null && this._userInfo.relationshipType.length > 0;
    }

    getRelationshipType(): string {
        return this._userInfo.relationshipType !== null && this._userInfo.relationshipType.length > 0 ? this._userInfo.relationshipType : null;
    }

    setRelationshipType(relationshipType: string): void {
        if (this._userInfo.authenticated) {
            this._userInfo.relationshipType = relationshipType;
            this.userInfoSubject.next(this._userInfo);
        }
    }

    clearRelationshipType(): void {
        if (this._userInfo.authenticated) {
            this._userInfo.relationshipType = null;
            this.userInfoSubject.next(this._userInfo);
        }
    }

    public getPublicIpAddress(): Observable<IpAddress> {
        const serviceUrl: string = this._apiSettings.publicIpAddressServiceUrl;
        if (this._logging.serviceCallTraceLog) {
            console.log('UserService.getPublicIpAddress :: serviceUrl = ' + serviceUrl);
        }

        return this._httpClient.get<IpAddress>(serviceUrl).pipe(
            retry(this._apiSettings.retryCount),
            catchError(this._handleError('getPublicIpAddress', { ip: '' }))
        );
    }

    private _getNewUserInfo(): UserInfo {
        const newUserInfo: UserInfo = {
            userId: null,
            userName: null,
            email: null,
            firstName: null,
            middleInitial: null,
            lastName: null,
            authenticated: false,
            securityQuestionSetupFlag: false,
            participantId: null,
            noteId: null,
            relationshipType: null,
            sessionToken: null,
            accessTokenExpiresAtUtc: null,
            sessionCreateDate: null,
            sessionLastActiveDate: null,
            sessionTimeout: false
        };
        return newUserInfo;
    }

    private hasValidAccessToken(userInfo: UserInfo): boolean {
        return userInfo.sessionToken !== null
            && userInfo.sessionToken.length > 0
            && userInfo.accessTokenExpiresAtUtc !== null
            && new Date(userInfo.accessTokenExpiresAtUtc).getTime() > Date.now();
    }

    private _onUserInfoChanged(newUserInfo: UserInfo): void {
        // this._userInfo = newUserInfo;
        this._sessionService.setObjectToSession(this.userInfoSessionName, newUserInfo);

        if (this._logging.userTraceLog) {
            console.log('UserService.onUserInfoChanged :: newUserInfo = ' + JSON.stringify(newUserInfo));
        }
    }

    private _onError(error: any) {
        if (this._logging.errorTraceLogging) {
            console.log('UserService._onError :: ' + JSON.stringify(error));
        }
        console.error(error);
    }
}

// **********************************************
// Route Guards
// **********************************************

@Injectable()
export class MaintenanceGuard implements CanActivate {
    private _logging = environment.logging;

    constructor(private _router: Router, private _userService: UserService) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        if (environment.isInMaintenanceMode) {
            if (this._logging.userTraceLog) {
                console.log('MaintenanceGuard.canActivate() = false');
            }
            this._router.navigate(['maintenance']);
            return false;
        } else {
            if (this._logging.userTraceLog) {
                console.log('MaintenanceGuard.canActivate() = true');
            }

            return true;
        }
    }
}

@Injectable()
export class OnlyLoggedInUsersGuard implements CanActivate {
    private _logging = environment.logging;

    constructor(private _router: Router, private _userService: UserService) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {

        let isLoggedIn: boolean = this._userService.isLoggedIn(false);

        if (isLoggedIn) {
            if (this._logging.userTraceLog) {
                console.log('OnlyLoggedInUsersGuard.canActivate() = true');
            }
            return true;
        }
        else {
            if (this._logging.userTraceLog) {
                console.log('OnlyLoggedInUsersGuard.canActivate() = false');
            }
            this._router.navigate(['login']);
            return false;
        }
    }
}

@Injectable()
export class OnlyHasSecurityQuestionUsersGuard implements CanActivate {
    private _logging = environment.logging;

    constructor(private _router: Router, private _userService: UserService) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        if (this._userService.hasSecurityQuestionSetupFlag()) {
            if (this._logging.userTraceLog) {
                console.log('OnlyHasSecurityQuestionUsersGuard.canActivate() = true');
            }
            return true;
        } else {
            if (this._logging.userTraceLog) {
                console.log('OnlyHasSecurityQuestionUsersGuard.canActivate() = false');
            }
            this._router.navigate(['login']);
            return false;
        }
    }
}

@Injectable()
export class OnlyHasParticipantIdUsersGuard implements CanActivate {
    private _logging = environment.logging;

    constructor(private _router: Router, private _userService: UserService) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        if (this._userService.hasParticipantId()) {
            if (this._logging.userTraceLog) {
                console.log('OnlyHasParticipantIdUsersGuard.canActivate() = true');
            }
            return true;
        } else {
            if (this._logging.userTraceLog) {
                console.log('OnlyHasParticipantIdUsersGuard.canActivate() = false');
            }
            this._router.navigate(['login']);
            return false;
        }
    }
}

@Injectable()
export class OnlyHasNoteIdRelationshipTypeUsersGuard implements CanActivate {
    private _logging = environment.logging;

    constructor(private _router: Router, private _userService: UserService) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        if (this._userService.hasNoteId() && this._userService.hasRelationshipType()) {
            if (this._logging.userTraceLog) {
                console.log('OnlyHasNoteIdRelationshipTypeUsersGuard.canActivate() = true');
            }
            return true;
        } else {
            if (this._logging.userTraceLog) {
                console.log('OnlyHasNoteIdRelationshipTypeUsersGuard.canActivate() = false');
            }
            this._router.navigate(['']);
            return false;
        }
    }
}
